import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import { createCoasterRuntime } from '../app/runtime.ts';
import type { CoasterRuntime, RuntimeSnapshot } from '../app/runtime-types.ts';

const INITIAL_SNAPSHOT: RuntimeSnapshot = {
  mode: 'menu',
  ready: false,
  status: 'Loading coaster…',
  selectedNodeId: null,
  selectedNodeLabel: null,
  selectedNodeIndex: null,
  nodeCount: 0,
  selectedHeightM: null,
  selectedPosition: null,
  canUndo: false,
  fixtureId: 'lift-drop',
  fixtureTitle: 'Lift & Drop Lab',
  telemetry: {
    speedMps: 0,
    speedMph: 0,
    verticalG: 1,
    lateralG: 0,
    longitudinalG: 0,
    peakSpeedMph: 0,
    activeSectionType: null,
    activeSectionLabel: 'Coasting',
  },
  ride: {
    phase: 'idle',
    progressPercent: 0,
    elapsedSeconds: 0,
    peakVerticalG: 1,
    peakVerticalProgressPercent: 0,
    peakLateralG: 0,
    peakLateralProgressPercent: 0,
    intensity: {
      level: 'comfortable',
      label: 'Comfortable',
      message: 'Forces stayed in the initial comfort band.',
      progressPercent: 0,
    },
    visitedSectionTypes: [],
  },
  lastRide: null,
  diagnostics: {
    qualityTier: 'high',
    frameMsP50: 0,
    frameMsP95: 0,
    rebuildMs: 0,
    changedSpans: 0,
    geometryRevision: 0,
    trackLengthM: 0,
    sampleCount: 0,
    rendererGeometries: 0,
    rendererTextures: 0,
    maxRailSeamGapM: 0,
    maxRailSeamAngleDegrees: 0,
    maxTieSpacingM: 0,
  },
  debugVisible: window.location.hash === '#/debug',
  persistenceAvailable: true,
};

function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

function mph(value: number): string {
  return Number.isFinite(value) ? `${value.toFixed(1)} mph` : '--';
}

function force(value: number): string {
  return Number.isFinite(value) ? `${value.toFixed(2)} G` : '--';
}

export function App() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const runtimeRef = useRef<CoasterRuntime | null>(null);
  const [state, setState] = useState<RuntimeSnapshot>(INITIAL_SNAPSHOT);
  const [confirmReset, setConfirmReset] = useState(false);
  const inRide = state.mode === 'ride-first-person' || state.mode === 'ride-third-person';
  const inEditor = state.mode === 'editor';
  const camera = state.mode === 'ride-third-person' ? 'third-person' : 'first-person';

  useEffect(() => {
    if (!canvasRef.current) return;
    const runtime = createCoasterRuntime(canvasRef.current);
    runtimeRef.current = runtime;
    const unsubscribe = runtime.subscribe(setState);
    return () => {
      unsubscribe();
      runtime.dispose();
      runtimeRef.current = null;
    };
  }, []);

  const statusTone = useMemo(() => {
    if (state.status.toLowerCase().includes('invalid')) return 'danger';
    if (
      state.status.toLowerCase().includes('unavailable') ||
      state.status.toLowerCase().includes('saving is off') ||
      state.status.toLowerCase().includes("couldn't save") ||
      state.status.toLowerCase().includes('stalled')
    )
      return 'warning';
    return 'neutral';
  }, [state.status]);

  return (
    <main class={`app-shell mode-${state.mode}`}>
      <header class="topbar">
        <div class="brand-lockup">
          <span class="eyebrow">Coaster Builder</span>
          <h1>Buncha Coasters</h1>
        </div>
        <div class="topbar-actions">
          <span class="fixture-name">
            <small>Coaster</small>
            <strong>{state.fixtureTitle}</strong>
          </span>
          {state.debugVisible && <span class="debug-badge">Debug Build</span>}
        </div>
      </header>

      <section class="world-shell" aria-label="Coaster design and ride viewport">
        <canvas ref={canvasRef} id="coaster-canvas" data-testid="coaster-canvas" />
        <div class="canvas-vignette" aria-hidden="true" />

        {state.mode === 'menu' && (
          <section class="menu-card" aria-labelledby="welcome-title">
            <span class="eyebrow">Quick Start</span>
            <h2 id="welcome-title">Build it. Ride it.</h2>
            <p>Move the track points, then take your coaster for a test ride.</p>
            <button
              class="primary-action"
              type="button"
              onClick={() => runtimeRef.current?.openEditor()}
            >
              Start Building
            </button>
            <small>Your build saves on this device.</small>
          </section>
        )}

        {inEditor && (
          <>
            <aside class="editor-tools" aria-label="Track editing tools">
              <span class="eyebrow">Edit Track</span>
              <h2>{state.selectedNodeLabel ?? 'Pick a Track Point'}</h2>
              <p class="tool-help">
                Pick a number, then drag its marker. Drag empty space to rotate. Scroll to zoom.
              </p>
              <div class="section-legend" aria-label="Track system colors">
                <span>Track systems</span>
                <div>
                  <small class="legend-station">Station</small>
                  <small class="legend-lift">Lift</small>
                  <small class="legend-launch">Launch</small>
                  <small class="legend-brake">Brakes</small>
                </div>
              </div>
              <div class="node-selector" role="group" aria-label="Select track point">
                {Array.from({ length: state.nodeCount }, (_, index) => (
                  <button
                    type="button"
                    class={state.selectedNodeIndex === index ? 'is-selected' : undefined}
                    aria-label={`Select track point ${index + 1}`}
                    aria-pressed={state.selectedNodeIndex === index}
                    onClick={() => runtimeRef.current?.selectNode(index)}
                  >
                    {index + 1}
                  </button>
                ))}
              </div>
              <div class="nudge-grid" aria-label="Move selected track point with camera view">
                <button
                  type="button"
                  aria-label="Move point forward"
                  onClick={() => runtimeRef.current?.nudgeSelected(0, 2)}
                >
                  <span>↑</span>
                  <small>Forward</small>
                </button>
                <button
                  type="button"
                  aria-label="Move point left"
                  onClick={() => runtimeRef.current?.nudgeSelected(-2, 0)}
                >
                  <span>←</span>
                  <small>Left</small>
                </button>
                <button
                  type="button"
                  aria-label="Move point right"
                  onClick={() => runtimeRef.current?.nudgeSelected(2, 0)}
                >
                  <span>→</span>
                  <small>Right</small>
                </button>
                <button
                  type="button"
                  aria-label="Move point back"
                  onClick={() => runtimeRef.current?.nudgeSelected(0, -2)}
                >
                  <span>↓</span>
                  <small>Back</small>
                </button>
              </div>
              <small class="camera-relative-note">2 m per tap · directions follow your view.</small>
              {state.selectedPosition && (
                <div class="position-readout" aria-label="Selected point position">
                  <span>X {state.selectedPosition.x.toFixed(1)}</span>
                  <span>Z {state.selectedPosition.z.toFixed(1)}</span>
                </div>
              )}
              <label class="elevation-control">
                <span>Height</span>
                <strong>{state.selectedHeightM?.toFixed(1) ?? '--'} m</strong>
                <input
                  data-testid="elevation-slider"
                  type="range"
                  min="1"
                  max="48"
                  step="0.5"
                  disabled={!state.selectedNodeId}
                  value={state.selectedHeightM ?? 1}
                  onPointerDown={() => runtimeRef.current?.beginEdit(true)}
                  onPointerUp={() => runtimeRef.current?.commitEdit()}
                  onPointerCancel={() => runtimeRef.current?.commitEdit()}
                  onKeyDown={() => runtimeRef.current?.beginEdit(true)}
                  onKeyUp={() => runtimeRef.current?.commitEdit()}
                  onBlur={() => runtimeRef.current?.commitEdit()}
                  onInput={(event) =>
                    runtimeRef.current?.setElevation(Number(event.currentTarget.value))
                  }
                />
              </label>
              <div class="edit-actions">
                <button
                  class="secondary-action"
                  type="button"
                  disabled={!state.canUndo}
                  onClick={() => runtimeRef.current?.undo()}
                >
                  Undo
                </button>
                <button
                  class="secondary-action danger-action"
                  type="button"
                  onClick={() => setConfirmReset(true)}
                >
                  Reset Track
                </button>
              </div>
              {confirmReset && (
                <div class="reset-confirm" role="alertdialog" aria-label="Reset Track?">
                  <strong>Reset the whole track?</strong>
                  <span>You can undo it afterward.</span>
                  <div>
                    <button type="button" onClick={() => setConfirmReset(false)}>
                      Keep Editing
                    </button>
                    <button
                      class="danger-action"
                      type="button"
                      onClick={() => {
                        runtimeRef.current?.resetFixture();
                        setConfirmReset(false);
                      }}
                    >
                      Confirm Reset
                    </button>
                  </div>
                </div>
              )}
            </aside>

            <div class="editor-primary">
              <div class="estimate" aria-label="Estimated ride measurements">
                <span>{state.diagnostics.trackLengthM.toFixed(0)} m track</span>
                <span>{state.persistenceAvailable ? 'Saved locally' : 'Saving off'}</span>
                {state.lastRide && (
                  <span>
                    Last ride: {state.lastRide.peakSpeedMph.toFixed(1)} mph ·{' '}
                    {state.lastRide.peakVerticalG.toFixed(1)} V G ·{' '}
                    {state.lastRide.peakLateralG.toFixed(1)} L G
                  </span>
                )}
                {state.lastRide && (
                  <span class={`intensity-text intensity-${state.lastRide.intensity.level}`}>
                    {state.lastRide.intensity.label} intensity · peak near{' '}
                    {state.lastRide.intensity.progressPercent.toFixed(0)}%
                  </span>
                )}
              </div>
              <button
                data-testid="test-ride"
                class="primary-action"
                type="button"
                onClick={() => runtimeRef.current?.startRide('first-person')}
              >
                Test Ride
              </button>
            </div>
          </>
        )}

        {inRide && (
          <>
            <div class="ride-telemetry" aria-label="Live ride telemetry">
              <div>
                <span>Speed</span>
                <strong data-testid="speed-value">{mph(state.telemetry.speedMph)}</strong>
              </div>
              <div>
                <span>Vertical G</span>
                <strong>{force(state.telemetry.verticalG)}</strong>
              </div>
              <div>
                <span>Lateral G</span>
                <strong>{force(state.telemetry.lateralG)}</strong>
              </div>
              <div>
                <span>Track</span>
                <strong>{state.ride.progressPercent.toFixed(0)}%</strong>
              </div>
            </div>
            <div class={`section-status section-${state.telemetry.activeSectionType ?? 'normal'}`}>
              <span>Track section</span>
              <strong data-testid="active-section">{state.telemetry.activeSectionLabel}</strong>
            </div>
            {state.ride.phase === 'running' ? (
              <div class="ride-controls">
                <div class="camera-toggle" role="group" aria-label="Ride camera">
                  <button
                    type="button"
                    aria-pressed={camera === 'first-person'}
                    onClick={() => runtimeRef.current?.setRideCamera('first-person')}
                  >
                    First person
                  </button>
                  <button
                    type="button"
                    aria-pressed={camera === 'third-person'}
                    onClick={() => runtimeRef.current?.setRideCamera('third-person')}
                  >
                    Third person
                  </button>
                </div>
                <button
                  class="secondary-action"
                  type="button"
                  onClick={() => runtimeRef.current?.returnToEditor()}
                >
                  Back to Build
                </button>
              </div>
            ) : (
              <section class="ride-result" aria-labelledby="ride-result-title">
                <span class="eyebrow">Test Result</span>
                <h2 id="ride-result-title">
                  {state.ride.phase === 'completed' ? 'Track Complete!' : 'Ride Stalled'}
                </h2>
                <p>
                  {state.ride.phase === 'completed'
                    ? 'Your train finished the circuit.'
                    : 'Lower the next hill or add more speed, then try again.'}
                </p>
                <div
                  class={`intensity-callout intensity-${state.ride.intensity.level}`}
                  data-testid="intensity-result"
                >
                  <strong>{state.ride.intensity.label} simulated intensity</strong>
                  <span>{state.ride.intensity.message}</span>
                  <small>
                    Strongest section near {state.ride.intensity.progressPercent.toFixed(0)}%.
                  </small>
                </div>
                <dl>
                  <div>
                    <dt>Top speed</dt>
                    <dd>{mph(state.telemetry.peakSpeedMph)}</dd>
                  </div>
                  <div>
                    <dt>Peak vertical G</dt>
                    <dd>{force(state.ride.peakVerticalG)}</dd>
                    <small>Near {state.ride.peakVerticalProgressPercent.toFixed(0)}%</small>
                  </div>
                  <div>
                    <dt>Peak lateral G</dt>
                    <dd>{force(state.ride.peakLateralG)}</dd>
                    <small>Near {state.ride.peakLateralProgressPercent.toFixed(0)}%</small>
                  </div>
                  <div>
                    <dt>Track reached</dt>
                    <dd>{state.ride.progressPercent.toFixed(0)}%</dd>
                  </div>
                </dl>
                <div class="ride-result-actions">
                  <button
                    class="primary-action"
                    type="button"
                    onClick={() => runtimeRef.current?.restartRide(camera)}
                  >
                    Ride Again
                  </button>
                  <button
                    class="secondary-action"
                    type="button"
                    onClick={() => runtimeRef.current?.returnToEditor()}
                  >
                    Back to Build
                  </button>
                </div>
              </section>
            )}
          </>
        )}

        {state.debugVisible && (
          <aside class="debug-panel" data-testid="debug-panel">
            <span class="eyebrow">Debug Stats</span>
            <dl>
              <div>
                <dt>Quality</dt>
                <dd>{state.diagnostics.qualityTier}</dd>
              </div>
              <div>
                <dt>Samples</dt>
                <dd>{state.diagnostics.sampleCount}</dd>
              </div>
              <div>
                <dt>Changed spans</dt>
                <dd>{state.diagnostics.changedSpans}</dd>
              </div>
              <div>
                <dt>Geometry rev</dt>
                <dd>{state.diagnostics.geometryRevision}</dd>
              </div>
              <div>
                <dt>Frame p50 / p95</dt>
                <dd>
                  {state.diagnostics.frameMsP50.toFixed(1)} /{' '}
                  {state.diagnostics.frameMsP95.toFixed(1)} ms
                </dd>
              </div>
              <div>
                <dt>Renderer</dt>
                <dd>
                  {state.diagnostics.rendererGeometries} geo / {state.diagnostics.rendererTextures}{' '}
                  tex
                </dd>
              </div>
              <div>
                <dt>Rail seam</dt>
                <dd data-testid="rail-seam-gap">
                  {state.diagnostics.maxRailSeamGapM.toExponential(1)} m
                </dd>
              </div>
              <div>
                <dt>Seam angle</dt>
                <dd>{state.diagnostics.maxRailSeamAngleDegrees.toFixed(3)}°</dd>
              </div>
              <div>
                <dt>Max tie gap</dt>
                <dd>{state.diagnostics.maxTieSpacingM.toFixed(3)} m</dd>
              </div>
              <div>
                <dt>Local draft</dt>
                <dd>{state.persistenceAvailable ? 'available' : 'unavailable'}</dd>
              </div>
              <div>
                <dt>Cloud writes</dt>
                <dd>unavailable (fail-soft)</dd>
              </div>
            </dl>
            <div class="debug-fixtures" role="group" aria-label="Debug coaster fixture">
              <button type="button" onClick={() => runtimeRef.current?.loadDebugFixture('systems')}>
                Systems Circuit
              </button>
              <button type="button" onClick={() => runtimeRef.current?.loadDebugFixture('stall')}>
                Stall Test
              </button>
            </div>
            <button
              type="button"
              onClick={() =>
                void runtimeRef.current
                  ?.captureEvidence()
                  .then((blob) => downloadBlob(blob, 'buncha-coasters-phase1-evidence.png'))
              }
            >
              Capture frame
            </button>
          </aside>
        )}

        <div class={`status-toast status-${statusTone}`} role="status">
          {state.status}
        </div>
      </section>
    </main>
  );
}
